import type { EnvironmentId, PullRequestListEntry } from "@cmmd-forge/contracts";

import { Button } from "../../components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "../../components/ui/empty";
import { PullRequestDetailPanel } from "../../components/pullRequest/PullRequestDetailPanel";
import type { PullRequestRightPanelSurface } from "../../rightPanelSurface";
import type {
  PullRequestInboxError,
  PullRequestInboxGroupSection,
} from "./pullRequests.inbox.logic";
import { PullRequestInboxLoading, PullRequestRows } from "./PullRequestsPage.rows";

interface PullRequestInboxProps {
  readonly groups: ReadonlyArray<PullRequestInboxGroupSection>;
  readonly activeRowKey: string | undefined;
  readonly environmentLabels: ReadonlyMap<EnvironmentId, string>;
  readonly state: "loading" | "partial" | "fatal" | "ready" | "unavailable";
  readonly errors: ReadonlyArray<PullRequestInboxError>;
  readonly hasMore: boolean;
  readonly isFetchingMore: boolean;
  readonly isRefreshing: boolean;
  readonly hasActiveFilters: boolean;
  readonly hasSearchQuery: boolean;
  readonly onLoadMore: () => void;
  readonly onRetry: () => void;
  readonly onClearFilters: () => void;
  readonly onOpen: (row: PullRequestListEntry & { readonly environmentId: EnvironmentId }) => void;
}

export function PullRequestInbox(props: PullRequestInboxProps) {
  const {
    groups,
    activeRowKey,
    environmentLabels,
    state,
    errors,
    hasMore,
    isFetchingMore,
    isRefreshing,
    hasActiveFilters,
    hasSearchQuery,
    onLoadMore,
    onRetry,
    onClearFilters,
    onOpen,
  } = props;
  const emptyState = pullRequestInboxEmptyState({
    state,
    errors,
    rowCount: groups.flatMap((group) => group.rows).length,
    onRetry,
    hasActiveFilters,
    hasSearchQuery,
    onClearFilters,
  });
  if (emptyState !== null) return emptyState;
  return (
    <div>
      {isRefreshing ? (
        <p className="mb-2 px-2 text-xs text-muted-foreground" aria-live="polite">
          Refreshing pull requests…
        </p>
      ) : null}
      {groups.map((group) => (
        <section key={group.label} className="mb-3 last:mb-0">
          <h2 className="px-3 pb-0.5 text-xs font-medium text-muted-foreground/70">
            {group.label}
          </h2>
          <PullRequestRows
            rows={group.rows}
            activeRowKey={activeRowKey}
            environmentLabels={environmentLabels}
            onOpen={onOpen}
          />
        </section>
      ))}
      <PullRequestLoadMore
        hasMore={hasMore}
        isFetchingMore={isFetchingMore}
        onLoadMore={onLoadMore}
      />
    </div>
  );
}

function PullRequestLoadMore(props: {
  readonly hasMore: boolean;
  readonly isFetchingMore: boolean;
  readonly onLoadMore: () => void;
}) {
  if (!props.hasMore) return null;
  return (
    <div className="flex justify-center pt-3">
      <Button
        type="button"
        variant="outline"
        disabled={props.isFetchingMore}
        onClick={props.onLoadMore}
      >
        {props.isFetchingMore ? "Loading more pull requests…" : "Load more pull requests"}
      </Button>
    </div>
  );
}

function pullRequestInboxEmptyState(input: {
  readonly state: PullRequestInboxProps["state"];
  readonly rowCount: number;
  readonly errors: PullRequestInboxProps["errors"];
  readonly onRetry: () => void;
  readonly hasActiveFilters: boolean;
  readonly hasSearchQuery: boolean;
  readonly onClearFilters: () => void;
}) {
  if (input.state === "loading") return <PullRequestInboxLoading />;
  if (input.rowCount > 0) return null;
  if (input.state === "fatal") {
    return (
      <InboxMessage
        title="Pull requests unavailable"
        detail="Command could not read the connected repositories. Check Filters for unavailable repositories, then retry."
        actionLabel="Retry"
        onAction={input.onRetry}
      />
    );
  }
  if (input.state === "partial") {
    return (
      <InboxMessage
        title="Some connected repositories are unavailable"
        detail="Check Filters for unavailable repositories, then retry when Command is available."
        actionLabel="Retry"
        onAction={input.onRetry}
      />
    );
  }
  return (
    <PullRequestEmptyMessage
      state={input.state}
      hasActiveFilters={input.hasActiveFilters}
      hasSearchQuery={input.hasSearchQuery}
      onClearFilters={input.onClearFilters}
      onRefresh={input.onRetry}
    />
  );
}

function PullRequestEmptyMessage(props: {
  readonly state: PullRequestInboxProps["state"];
  readonly hasActiveFilters: boolean;
  readonly hasSearchQuery: boolean;
  readonly onClearFilters: () => void;
  readonly onRefresh: () => void;
}) {
  const message = emptyInboxMessage(props);
  return (
    <InboxMessage
      title={message.title}
      detail={message.detail}
      actionLabel={message.actionLabel}
      onAction={message.onAction}
    />
  );
}

function emptyInboxMessage(props: {
  readonly state: PullRequestInboxProps["state"];
  readonly hasActiveFilters: boolean;
  readonly hasSearchQuery: boolean;
  readonly onClearFilters: () => void;
  readonly onRefresh: () => void;
}) {
  if (props.state === "unavailable") {
    return {
      title: "No connected pull request repositories",
      detail: "Connect a source-control repository in Command to review changes here.",
      actionLabel: "Refresh",
      onAction: props.onRefresh,
    };
  }
  if (props.hasSearchQuery) {
    return {
      title: "No pull requests match this search",
      detail: "Try a shorter search or clear it to return to the inbox.",
      actionLabel: "Clear filters",
      onAction: props.onClearFilters,
    };
  }
  if (props.hasActiveFilters) {
    return {
      title: "No pull requests match these filters",
      detail: "Clear the filters to return to the full inbox.",
      actionLabel: "Clear filters",
      onAction: props.onClearFilters,
    };
  }
  return {
    title: "No pull requests yet",
    detail: "",
    actionLabel: "Refresh",
    onAction: props.onRefresh,
  };
}

interface InboxMessageProps {
  readonly title: string;
  readonly detail: string;
  readonly actionLabel?: string;
  readonly onAction?: () => void;
}

function InboxMessage({ title, detail, actionLabel, onAction }: InboxMessageProps) {
  return (
    <Empty className="py-16">
      <PullRequestBranchMark joined={false} />
      <EmptyHeader>
        <EmptyTitle>{title}</EmptyTitle>
        {detail.length > 0 ? <EmptyDescription>{detail}</EmptyDescription> : null}
      </EmptyHeader>
      {actionLabel === undefined || onAction === undefined ? null : (
        <EmptyContent>
          <Button type="button" variant="outline" onClick={onAction}>
            {actionLabel}
          </Button>
        </EmptyContent>
      )}
    </Empty>
  );
}

function PullRequestBranchMark({ joined }: { readonly joined: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 120 72"
      className="h-20 w-32 text-muted-foreground/60"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 58h100" className="text-muted-foreground/30" />
      <circle cx="10" cy="58" r="5" fill="currentColor" fillOpacity={0.25} />
      <circle cx="110" cy="58" r="5" fill="currentColor" fillOpacity={0.25} />
      {joined ? (
        <path d="M30 58c0-18 8-26 24-26h12c16 0 24 8 24 26" />
      ) : (
        <>
          <path d="M30 58c0-18 8-26 24-26h4" />
          <path d="M90 58c0-18-8-26-24-26h-4" strokeDasharray="2 7" />
        </>
      )}
      <circle
        cx="60"
        cy="32"
        r={joined ? 5 : 4}
        fill={joined ? "currentColor" : "none"}
        fillOpacity={0.25}
      />
    </svg>
  );
}

export function PullRequestPanel({ surface }: { readonly surface: PullRequestRightPanelSurface }) {
  return (
    <PullRequestDetailPanel
      environmentId={surface.environmentId}
      reference={{
        projectId: surface.projectId,
        ...(surface.repositoryLinkId === null
          ? {}
          : { projectRepositoryId: Number(surface.repositoryLinkId) }),
        repository: surface.repository,
        number: surface.number,
      }}
      context="page"
    />
  );
}
